import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ArrowLeft, ListChecks, UserCirclePlus, WarningOctagon, Clock, CheckCircle,
  AirplaneTilt, Anchor, CaretDown, CaretUp, Lightning, CalendarBlank, User, ArrowSquareOut, Camera, PaperPlaneTilt
} from '@phosphor-icons/react';

const API = '/api';

const PHASE_ORDER = { immediate: 0, early: 1, detailed: 2 };
const PHASE_LABELS = { immediate: 'Immediate (0-5 days)', early: 'Early (1-4 weeks)', detailed: 'Detailed (1-6 weeks)' };
const PHASE_COLORS = { immediate: 'bg-red-50 border-red-200', early: 'bg-amber-50 border-amber-200', detailed: 'bg-blue-50 border-blue-200' };

export default function InvestigationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.role === 'iic';

  const [investigation, setInvestigation] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [investigators, setInvestigators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [collapsedPhases, setCollapsedPhases] = useState({});

  // Assign dialog
  const [assignTask, setAssignTask] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);

  // Extend dialog
  const [extendTask, setExtendTask] = useState(null);
  const [extendDate, setExtendDate] = useState('');
  const [extendReason, setExtendReason] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [invRes, tasksRes] = await Promise.all([
        axios.get(`${API}/investigations/${id}`, { withCredentials: true }),
        axios.get(`${API}/investigations/${id}/tasks`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setInvestigation(invRes.data);
      setTasks(tasksRes.data);
      if (canManage) {
        const wlRes = await axios.get(`${API}/investigators/workload`, { withCredentials: true }).catch(() => ({ data: [] }));
        setInvestigators(wlRes.data);
      }
    } catch (e) {
      console.error('Failed to load investigation data:', e);
    } finally {
      setLoading(false);
    }
  }, [id, canManage]);

  useEffect(() => { loadData(); }, [loadData]);

  const generateTasks = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/investigations/${id}/tasks/generate`, {}, { withCredentials: true });
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error generating tasks');
    } finally {
      setGenerating(false);
    }
  };

  const handleAssign = async (taskCode, email) => {
    try {
      await axios.put(`${API}/investigations/${id}/tasks/assign`, {
        tasks: [{ task_code: taskCode, assigned_to: email }]
      }, { withCredentials: true });
      setAssignTask(null);
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error assigning task');
    }
  };

  const handleExtend = async () => {
    if (!extendTask || !extendDate || !extendReason) return;
    try {
      await axios.put(`${API}/tasks/${extendTask.id}/extend`, {
        new_due_date: extendDate,
        reason: extendReason
      }, { withCredentials: true });
      setExtendTask(null);
      setExtendDate('');
      setExtendReason('');
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error extending timeline');
    }
  };

  const handleStatusUpdate = async (taskId, status) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/status`, { status }, { withCredentials: true });
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error updating task');
    }
  };

  const togglePhase = (phase) => {
    setCollapsedPhases(p => ({ ...p, [phase]: !p[phase] }));
  };

  // Group tasks by phase
  const tasksByPhase = {};
  tasks.forEach(t => {
    const phase = t.phase || 'detailed';
    if (!tasksByPhase[phase]) tasksByPhase[phase] = [];
    tasksByPhase[phase].push(t);
  });
  const sortedPhases = Object.keys(tasksByPhase).sort((a, b) => (PHASE_ORDER[a] || 99) - (PHASE_ORDER[b] || 99));

  const escalatedTasks = tasks.filter(t => t.escalated && t.status !== 'completed');
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const totalTasks = tasks.length;
  const progressPct = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

  const getPriorityColor = (p) => {
    const map = { critical: 'bg-red-600 text-white', high: 'bg-red-100 text-red-800', medium: 'bg-amber-100 text-amber-800', low: 'bg-green-100 text-green-800' };
    return map[p] || 'bg-slate-100 text-slate-800';
  };
  const getStatusColor = (s) => {
    const map = { pending: 'bg-slate-200 text-slate-700', in_progress: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', escalated: 'bg-red-100 text-red-800', overdue: 'bg-red-100 text-red-800' };
    return map[s] || 'bg-slate-100 text-slate-800';
  };
  const getWorkloadColor = (wl) => {
    if (wl <= 3) return 'text-green-600';
    if (wl <= 8) return 'text-amber-600';
    return 'text-red-600';
  };

  // Find best match investigators for a task
  const getMatchedInvestigators = (task) => {
    return investigators.map(inv => {
      let matchScore = 0;
      if (inv.primary_specialty === task.specialist_required) matchScore += 3;
      if (inv.specializations?.includes(task.specialist_required)) matchScore += 2;
      if (inv.years_of_experience >= 5) matchScore += 1;
      return { ...inv, matchScore };
    }).sort((a, b) => b.matchScore - a.matchScore || a.workload_score - b.workload_score);
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-slate-500">Loading investigation...</div></div>;
  if (!investigation) return <div className="p-8 text-center text-slate-500">Investigation not found</div>;

  return (
    <div className="" data-testid="investigation-detail">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => navigate('/investigations')} data-testid="back-btn">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{investigation.investigation_number}</h1>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${investigation.sector === 'aviation' ? 'bg-blue-100 text-blue-800' : 'bg-cyan-100 text-cyan-800'}`}>
              {investigation.sector === 'aviation' ? <AirplaneTilt size={12} className="inline mr-1" /> : <Anchor size={12} className="inline mr-1" />}
              {investigation.sector === 'aviation' ? 'Aviation' : 'Maritime'}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(investigation.priority)}`}>{investigation.priority}</span>
          </div>
          <p className="text-sm text-slate-500 mt-1">Incident: {investigation.incident_number} | Lead: {investigation.lead_investigator || 'Unassigned'}</p>
        </div>
        {canManage && tasks.length === 0 && (
          <Button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg" onClick={generateTasks} disabled={generating} data-testid="generate-tasks-btn">
            <ListChecks size={18} className="mr-2" />
            {generating ? 'Generating...' : 'Generate ICAO Tasks'}
          </Button>
        )}
        {canManage && tasks.length > 0 && (
          <Button
            className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-lg"
            onClick={() => navigate(`/investigations/${id}/synthesis`)}
            data-testid="open-synthesis-btn"
          >
            <ListChecks size={18} className="mr-2" />
            Information Synthesis
          </Button>
        )}
        <Button
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg"
          onClick={() => navigate(`/investigations/${id}/fishbone`)}
          data-testid="open-fishbone-btn"
        >
          <ListChecks size={18} className="mr-2" />
          Fishbone Analysis
        </Button>
        <Button
          className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-lg"
          onClick={() => navigate(`/investigations/${id}/timeline`)}
          data-testid="open-timeline-btn"
        >
          <Clock size={18} className="mr-2" />
          Timeline View
        </Button>
        {canManage && tasks.length > 0 && (
          <Button
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg"
            onClick={() => navigate(`/investigations/${id}/report`)}
            data-testid="open-report-btn"
          >
            <ListChecks size={18} className="mr-2" />
            Final Report
          </Button>
        )}
        {investigation.sector !== 'maritime' && (
          <Button
            className="bg-gradient-to-r from-slate-700 to-slate-900 text-white rounded-lg"
            onClick={() => navigate(`/investigations/${id}/adrep`)}
            data-testid="open-adrep-btn"
          >
            <PaperPlaneTilt size={18} className="mr-2" />
            ECCAIRS / ADREP
          </Button>
        )}
        <Button
          className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white rounded-lg"
          onClick={() => navigate(`/ar-identify?investigation=${investigation.investigation_number || investigation.id}&sector=${investigation.sector || 'aviation'}`)}
          data-testid="open-ar-btn"
        >
          <Camera size={18} className="mr-2" />
          AR Identify
        </Button>
      </div>

      {/* Stats bar */}
      {tasks.length > 0 && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{totalTasks}</div>
            <div className="text-xs text-slate-500">Total Tasks</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Outfit' }}>{tasks.filter(t => t.assigned_to).length}</div>
            <div className="text-xs text-slate-500">Assigned</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <div className="text-2xl font-bold text-purple-600" style={{ fontFamily: 'Outfit' }}>{tasks.filter(t => t.status === 'in_progress').length}</div>
            <div className="text-xs text-slate-500">In Progress</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <div className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Outfit' }}>{completedCount}</div>
            <div className="text-xs text-slate-500">Completed</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <div className={`text-2xl font-bold ${escalatedTasks.length > 0 ? 'text-red-600' : 'text-slate-400'}`} style={{ fontFamily: 'Outfit' }}>{escalatedTasks.length}</div>
            <div className="text-xs text-slate-500">Escalated</div>
          </div>
        </div>
      )}

      {/* Progress bar */}
      {tasks.length > 0 && (
        <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Investigation Progress</span>
            <span className="text-sm font-bold text-slate-900">{progressPct}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2.5">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      {/* Escalation Alert */}
      {escalatedTasks.length > 0 && canManage && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6" data-testid="escalation-alert">
          <div className="flex items-center gap-2 mb-3">
            <WarningOctagon size={22} weight="fill" className="text-red-600" />
            <h3 className="text-sm font-bold text-red-900">ESCALATION ALERT - {escalatedTasks.length} task(s) exceeded 115% of standard timeline</h3>
          </div>
          <div className="space-y-2">
            {escalatedTasks.map(t => (
              <div key={t.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-red-200">
                <div>
                  <span className="font-mono text-xs text-red-600 font-bold">{t.task_code}</span>
                  <span className="mx-2 text-sm font-medium text-slate-800">{t.title}</span>
                  <span className="text-xs text-red-600">{t.days_remaining !== undefined ? `${Math.abs(t.days_remaining)} days overdue` : ''}</span>
                  {t.assigned_to_name && <span className="text-xs text-slate-500 ml-2">({t.assigned_to_name})</span>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-lg h-8 px-3 bg-amber-500 hover:bg-amber-600 text-white text-xs" onClick={() => { setExtendTask(t); setExtendDate(''); setExtendReason(''); }} data-testid={`extend-${t.task_code}`}>
                    <Clock size={12} className="mr-1" /> Extend
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Task list by phase */}
      {tasks.length > 0 ? (
        <div className="space-y-4">
          {sortedPhases.map(phase => (
            <div key={phase} className={`rounded-xl border ${PHASE_COLORS[phase] || 'bg-slate-50 border-slate-200'}`}>
              <button onClick={() => togglePhase(phase)} className="w-full flex items-center justify-between p-4 text-left" data-testid={`phase-${phase}`}>
                <div className="flex items-center gap-2">
                  {collapsedPhases[phase] ? <CaretDown size={16} /> : <CaretUp size={16} />}
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">{PHASE_LABELS[phase] || phase}</h3>
                  <span className="text-xs text-slate-500">({tasksByPhase[phase].length} tasks)</span>
                </div>
                <div className="flex gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">{tasksByPhase[phase].filter(t => t.status === 'completed').length} done</span>
                  {tasksByPhase[phase].filter(t => t.escalated && t.status !== 'completed').length > 0 && (
                    <span className="px-2 py-0.5 rounded bg-red-100 text-red-700">{tasksByPhase[phase].filter(t => t.escalated).length} escalated</span>
                  )}
                </div>
              </button>
              {!collapsedPhases[phase] && (
                <div className="px-4 pb-4 space-y-2">
                  {tasksByPhase[phase].map(task => (
                    <div key={task.id} className={`bg-white rounded-lg border p-4 ${task.escalated && task.status !== 'completed' ? 'border-red-300 ring-1 ring-red-200' : 'border-slate-200'}`} data-testid={`task-${task.task_code}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded w-[52px] text-center inline-block shrink-0">{task.task_code}</span>
                            <span className="text-sm font-semibold text-slate-900">{task.title}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(task.status)}`}>{task.status?.replace('_', ' ')}</span>
                            {task.escalated && task.status !== 'completed' && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-600 text-white flex items-center gap-0.5">
                                <Lightning size={10} /> ESCALATED
                              </span>
                            )}
                            {task.timeline_extended && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">Extended</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{task.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><User size={12} /> Specialist: <span className="font-medium text-slate-700">{task.specialist_required}</span></span>
                            <span className="flex items-center gap-1"><CalendarBlank size={12} /> Start: {task.start_date ? new Date(task.start_date).toLocaleDateString() : '-'}</span>
                            <span className="flex items-center gap-1"><Clock size={12} /> End: {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}</span>
                            <span className="text-slate-400">({task.standard_days}d standard)</span>
                            {task.days_remaining !== undefined && (
                              <span className={task.days_remaining < 0 ? 'text-red-600 font-bold' : task.days_remaining <= 3 ? 'text-amber-600 font-medium' : 'text-green-600'}>
                                ({task.days_remaining < 0 ? `${Math.abs(task.days_remaining)}d overdue` : `${task.days_remaining}d left`})
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="outline" className="rounded-lg h-8 px-3 text-xs border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => navigate(`/tasks/${task.id}`)} data-testid={`open-module-${task.task_code}`} title="Open Specialist Module">
                            <ArrowSquareOut size={14} className="mr-1" /> Module
                          </Button>
                          {task.assigned_to ? (
                            <div className="text-right">
                              <div className="text-xs font-medium text-slate-800">{task.assigned_to_name || task.assigned_to}</div>
                              <div className="text-[10px] text-slate-400">{task.assigned_to}</div>
                            </div>
                          ) : canManage ? (
                            <Button size="sm" className="rounded-lg h-8 px-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs" onClick={() => setAssignTask(task)} data-testid={`assign-task-${task.task_code}`}>
                              <UserCirclePlus size={14} className="mr-1" /> Assign
                            </Button>
                          ) : (
                            <span className="text-xs text-amber-600">Unassigned</span>
                          )}
                          {canManage && task.status === 'in_progress' && (
                            <Button size="sm" variant="outline" className="rounded-lg h-8 px-2 text-xs text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleStatusUpdate(task.id, 'completed')} data-testid={`complete-${task.task_code}`}>
                              <CheckCircle size={14} />
                            </Button>
                          )}
                          {canManage && task.escalated && task.status !== 'completed' && (
                            <Button size="sm" className="rounded-lg h-8 px-3 bg-amber-500 hover:bg-amber-600 text-white text-xs" onClick={() => { setExtendTask(task); setExtendDate(''); setExtendReason(''); }}>
                              <Clock size={12} className="mr-1" /> Extend
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <ListChecks size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No tasks generated yet</h3>
          <p className="text-sm text-slate-500 mb-4">Generate ICAO 9756 standard investigation tasks to begin team assignment.</p>
          {canManage && (
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg" onClick={generateTasks} disabled={generating}>
              <ListChecks size={18} className="mr-2" />
              {generating ? 'Generating...' : 'Generate ICAO Tasks'}
            </Button>
          )}
        </div>
      )}

      {/* Assign Task Dialog */}
      <Dialog open={!!assignTask} onOpenChange={(open) => { if (!open) setAssignTask(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              Assign Task: {assignTask?.task_code}
              <span className="text-sm font-normal text-slate-500 ml-2">- {assignTask?.title}</span>
            </DialogTitle>
          </DialogHeader>
          {assignTask && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm">
                <p className="text-blue-900"><strong>Required Specialist:</strong> {assignTask.specialist_required}</p>
                <p className="text-blue-800 text-xs mt-1">Investigators are ranked by specialist match, then workload. Green = best match.</p>
              </div>
              <div className="space-y-2">
                {getMatchedInvestigators(assignTask).map((inv) => (
                  <div key={inv.email} className={`flex items-center justify-between p-3 border rounded-lg transition-all ${inv.matchScore >= 3 ? 'border-green-300 bg-green-50/50' : inv.matchScore >= 2 ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`assign-opt-${inv.email}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-900">{inv.name}</span>
                        {inv.matchScore >= 3 && <span className="px-1.5 py-0.5 text-[10px] bg-green-600 text-white rounded font-bold">BEST MATCH</span>}
                        {inv.matchScore >= 2 && inv.matchScore < 3 && <span className="px-1.5 py-0.5 text-[10px] bg-blue-600 text-white rounded">SPECIALIST</span>}
                        <span className="px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded capitalize">{inv.role}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {inv.primary_specialty && <span className="mr-2">Specialty: {inv.primary_specialty}</span>}
                        {inv.years_of_experience > 0 && <span className="mr-2">{inv.years_of_experience} yrs exp</span>}
                        {inv.investigations_completed > 0 && <span>{inv.investigations_completed} investigations</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-3">
                      <div className="text-center">
                        <div className={`text-sm font-bold ${getWorkloadColor(inv.workload_score)}`}>{inv.workload_score}</div>
                        <div className="text-[9px] text-slate-400">LOAD</div>
                      </div>
                      <div className="text-[10px] text-slate-500 text-center w-16">
                        <div>{inv.total_active_tasks} tasks</div>
                        <div>{inv.escalated_tasks > 0 ? <span className="text-red-600">{inv.escalated_tasks} esc</span> : `${inv.completed_tasks} done`}</div>
                      </div>
                      <Button size="sm" className="rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs" onClick={() => handleAssign(assignTask.task_code, inv.email)} data-testid={`assign-confirm-${inv.email}`}>
                        Assign
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extend Timeline Dialog */}
      <Dialog open={!!extendTask} onOpenChange={(open) => { if (!open) setExtendTask(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Extend Timeline: {extendTask?.task_code}</DialogTitle>
          </DialogHeader>
          {extendTask && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm">
                <p className="text-amber-900"><strong>{extendTask.title}</strong></p>
                <p className="text-amber-700 text-xs mt-1">
                  Standard: {extendTask.standard_days} days | Start: {extendTask.start_date ? new Date(extendTask.start_date).toLocaleDateString() : '-'} | End: {new Date(extendTask.due_date).toLocaleDateString()}
                  {extendTask.days_remaining !== undefined && extendTask.days_remaining < 0 && <span className="text-red-600"> ({Math.abs(extendTask.days_remaining)} days overdue)</span>}
                </p>
              </div>
              <div>
                <Label>New Due Date *</Label>
                <Input type="date" value={extendDate} onChange={(e) => setExtendDate(e.target.value)} className="rounded-lg" data-testid="extend-date" />
              </div>
              <div>
                <Label>Reason for Extension *</Label>
                <Textarea value={extendReason} onChange={(e) => setExtendReason(e.target.value)} placeholder="Justify timeline extension..." className="rounded-lg min-h-[80px]" data-testid="extend-reason" />
              </div>
              <div className="flex gap-3">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg" onClick={handleExtend} disabled={!extendDate || !extendReason} data-testid="extend-confirm">
                  Extend Timeline
                </Button>
                <Button variant="outline" className="rounded-lg" onClick={() => setExtendTask(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
